import type {
  AttemptResult,
  DomainMastery,
  LevelDefinition,
  ProgressRepository
} from "@/domain";
import type { ActivityHostCompletionMetrics } from "@/features/activity-host";
import {
  calculateMasteryForProfile,
  rankLevelsForExploration,
  type RankedLevel
} from "@/features/progress/recommendation";
import {
  awardCompletedTaskRewards,
  type RewardUpdate
} from "@/features/progress/rewards";
import {
  completeSessionTask,
  type DailySession,
  type TaskCompletionResult
} from "@/features/progress/session";

import { createAttemptResult } from "./attempt-result";

export type AttemptNavigationReadiness =
  | "blocked"
  | "durable"
  | "temporary";

export type AttemptSaveStage =
  | "save-attempt"
  | "load-attempts"
  | "save-mastery"
  | "load-rewards"
  | "save-rewards"
  | "derive-progress";

export interface AttemptGeneration {
  readonly id: number;
  readonly levelId: string;
  readonly profileId: string;
}

export interface AttemptSaveContext<TPayload = unknown> {
  readonly profileId: string;
  readonly level: LevelDefinition<TPayload>;
  readonly recommendationLevels: readonly LevelDefinition<TPayload>[];
  readonly dailySession?: DailySession | null;
}

export interface AttemptProgressUpdate<TPayload = unknown> {
  readonly attempt: AttemptResult;
  readonly mastery: readonly DomainMastery[];
  readonly rewardUpdate: RewardUpdate;
  readonly sessionProgress: TaskCompletionResult | null;
  readonly nextRecommendation: RankedLevel<TPayload> | null;
}

export interface AttemptSaveIdleState {
  readonly status: "idle";
  readonly generation: AttemptGeneration | null;
  readonly navigation: "blocked";
}

export interface AttemptSaveSavingState {
  readonly status: "saving";
  readonly generation: AttemptGeneration;
  readonly attempt: AttemptResult;
  readonly navigation: "blocked";
}

export interface AttemptSaveSavedState<TPayload = unknown> {
  readonly status: "saved";
  readonly generation: AttemptGeneration;
  readonly progress: AttemptProgressUpdate<TPayload>;
  readonly navigation: "durable";
}

export interface AttemptSaveTemporaryState<TPayload = unknown> {
  readonly status: "temporary";
  readonly generation: AttemptGeneration;
  readonly progress: AttemptProgressUpdate<TPayload>;
  readonly navigation: "temporary";
}

export interface AttemptSaveErrorState {
  readonly status: "error";
  readonly generation: AttemptGeneration;
  readonly attempt: AttemptResult;
  readonly attemptSaveConfirmed: boolean;
  readonly error: Error;
  readonly failedStage: AttemptSaveStage;
  readonly navigation: "blocked";
  readonly recoverable: true;
}

export type AttemptSaveSettledState<TPayload = unknown> =
  | AttemptSaveSavedState<TPayload>
  | AttemptSaveTemporaryState<TPayload>
  | AttemptSaveErrorState;

export type AttemptSaveSnapshot<TPayload = unknown> =
  | AttemptSaveIdleState
  | AttemptSaveSavingState
  | AttemptSaveSettledState<TPayload>;

export interface StaleAttemptCompletion {
  readonly status: "stale";
  readonly generation: AttemptGeneration;
}

export type AttemptSaveCommandResult<TPayload = unknown> =
  | AttemptSaveSettledState<TPayload>
  | StaleAttemptCompletion;

export interface AttemptSaveControllerOptions {
  readonly repository: ProgressRepository;
  readonly isPersistent: boolean;
  readonly createAttemptId?: () => string;
  readonly now?: () => Date;
}

interface GenerationRecord<TPayload> {
  readonly generation: AttemptGeneration;
  readonly context: AttemptSaveContext<TPayload>;
}

interface SaveOperation<TPayload> extends GenerationRecord<TPayload> {
  readonly attempt: AttemptResult;
  // Checkpoints retain exact write targets so an ambiguous failure is retried
  // idempotently instead of recalculating mastery or adding another reward.
  attemptSaveConfirmed: boolean;
  attempts: AttemptResult[] | null;
  mastery: DomainMastery[] | null;
  masterySaveConfirmed: boolean;
  rewardUpdate: RewardUpdate | null;
  rewardsSaveConfirmed: boolean;
  stage: AttemptSaveStage;
  inFlight: Promise<AttemptSaveCommandResult<TPayload>> | null;
  settled: AttemptSaveSettledState<TPayload> | null;
}

interface Deferred<T> {
  readonly promise: Promise<T>;
  readonly resolve: (value: T | PromiseLike<T>) => void;
  readonly reject: (reason?: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
  let resolve: Deferred<T>["resolve"] = () => undefined;
  let reject: Deferred<T>["reject"] = () => undefined;
  const promise = new Promise<T>((promiseResolve, promiseReject) => {
    resolve = promiseResolve;
    reject = promiseReject;
  });

  return { promise, resolve, reject };
}

function createDefaultAttemptId(): string {
  return "attempt-" + globalThis.crypto.randomUUID();
}

function cloneDailySession(session: DailySession): DailySession {
  return {
    ...session,
    plan: { ...session.plan, levelIds: [...session.plan.levelIds] },
    completedLevelIds: [...session.completedLevelIds]
  };
}

function toError(error: unknown): Error {
  return error instanceof Error
    ? error
    : new Error("Attempt progress could not be saved.", { cause: error });
}

export class PendingAttemptSaveError extends Error {
  override readonly name = "PendingAttemptSaveError";

  constructor(profileId: string) {
    super(
      'Profile "' +
        profileId +
        '" already has an attempt that must finish or be retried.'
    );
  }
}

export class AttemptSaveController<TPayload = unknown> {
  private readonly repository: ProgressRepository;
  private readonly isPersistent: boolean;
  private readonly createAttemptId: () => string;
  private readonly now: () => Date;
  private readonly listeners = new Set<() => void>();
  private readonly operations = new WeakMap<
    AttemptGeneration,
    SaveOperation<TPayload>
  >();
  private readonly pendingByProfile = new Map<
    string,
    SaveOperation<TPayload>
  >();
  private generationSequence = 0;
  private current: GenerationRecord<TPayload> | null = null;
  private snapshot: AttemptSaveSnapshot<TPayload> = {
    status: "idle",
    generation: null,
    navigation: "blocked"
  };

  constructor({
    repository,
    isPersistent,
    createAttemptId = createDefaultAttemptId,
    now = () => new Date()
  }: AttemptSaveControllerOptions) {
    this.repository = repository;
    this.isPersistent = isPersistent;
    this.createAttemptId = createAttemptId;
    this.now = now;
  }

  readonly getSnapshot = (): AttemptSaveSnapshot<TPayload> => this.snapshot;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    let isSubscribed = true;

    return () => {
      if (isSubscribed) {
        isSubscribed = false;
        this.listeners.delete(listener);
      }
    };
  };

  readonly beginAttempt = (
    context: AttemptSaveContext<TPayload>
  ): AttemptGeneration => {
    // Same-profile saves are serialized at the navigation boundary. Different
    // profiles may proceed independently without sharing derived state.
    if (this.pendingByProfile.has(context.profileId)) {
      throw new PendingAttemptSaveError(context.profileId);
    }

    if (
      context.dailySession !== undefined &&
      context.dailySession !== null &&
      context.dailySession.plan.profileId !== context.profileId
    ) {
      throw new TypeError(
        "The daily session and attempt must belong to the same profile."
      );
    }

    const generation = Object.freeze({
      id: ++this.generationSequence,
      levelId: context.level.id,
      profileId: context.profileId
    });
    const dailySession = context.dailySession;
    const stableContext: AttemptSaveContext<TPayload> = {
      profileId: context.profileId,
      level: context.level,
      recommendationLevels: [...context.recommendationLevels],
      ...(dailySession === undefined
        ? {}
        : {
            dailySession:
              dailySession === null ? null : cloneDailySession(dailySession)
          })
    };

    this.current = { generation, context: stableContext };
    this.publish({
      status: "idle",
      generation,
      navigation: "blocked"
    });

    return generation;
  };

  readonly invalidate = (generation: AttemptGeneration): void => {
    if (!this.isCurrent(generation)) {
      return;
    }

    // Route departure only releases presentation ownership. An unfinished
    // operation stays profile-indexed so a later failure can still be found.
    this.current = null;
    this.publish({
      status: "idle",
      generation: null,
      navigation: "blocked"
    });
  };

  readonly recoverPending = (
    profileId: string
  ): AttemptGeneration | null => {
    const operation = this.pendingByProfile.get(profileId);

    if (
      operation === undefined ||
      operation.inFlight !== null ||
      operation.settled?.status !== "error"
    ) {
      return null;
    }

    if (!this.isCurrent(operation.generation)) {
      this.current = operation;
      this.publish(operation.settled);
    }

    return operation.generation;
  };

  readonly completeAttempt = (
    generation: AttemptGeneration,
    metrics: ActivityHostCompletionMetrics
  ): Promise<AttemptSaveCommandResult<TPayload>> => {
    const existingOperation = this.operations.get(generation);

    if (existingOperation !== undefined) {
      return this.readOperationResult(existingOperation);
    }

    if (!this.isCurrent(generation)) {
      return Promise.resolve(this.staleResult(generation));
    }

    const current = this.current;
    if (current === null) {
      return Promise.resolve(this.staleResult(generation));
    }

    const attempt = createAttemptResult(current.context, metrics, {
      attemptId: this.createAttemptId(),
      completedAt: this.now()
    });
    const operation: SaveOperation<TPayload> = {
      ...current,
      attempt,
      attemptSaveConfirmed: false,
      attempts: null,
      mastery: null,
      masterySaveConfirmed: false,
      rewardUpdate: null,
      rewardsSaveConfirmed: false,
      stage: "save-attempt",
      inFlight: null,
      settled: null
    };

    this.operations.set(generation, operation);
    this.pendingByProfile.set(current.context.profileId, operation);
    return this.runOperation(operation);
  };

  readonly retry = (
    generation: AttemptGeneration
  ): Promise<AttemptSaveCommandResult<TPayload>> => {
    const operation = this.operations.get(generation);

    if (operation === undefined) {
      return Promise.resolve(this.staleResult(generation));
    }

    if (operation.inFlight !== null) {
      return operation.inFlight;
    }

    if (!this.isCurrent(generation)) {
      return Promise.resolve(this.staleResult(generation));
    }

    if (operation.settled?.status !== "error") {
      return this.readOperationResult(operation);
    }

    return this.runOperation(operation);
  };

  private readOperationResult(
    operation: SaveOperation<TPayload>
  ): Promise<AttemptSaveCommandResult<TPayload>> {
    if (operation.inFlight !== null) {
      return operation.inFlight;
    }

    if (operation.settled !== null) {
      return Promise.resolve(
        this.resultForCurrentGeneration(operation, operation.settled)
      );
    }

    return this.runOperation(operation);
  }

  private runOperation(
    operation: SaveOperation<TPayload>
  ): Promise<AttemptSaveCommandResult<TPayload>> {
    if (operation.inFlight !== null) {
      return operation.inFlight;
    }

    const deferred = createDeferred<AttemptSaveCommandResult<TPayload>>();
    const tracked = deferred.promise;

    // Install the command promise before notifying subscribers. A synchronous
    // completion callback during the saving notification must coalesce here.
    operation.inFlight = tracked;

    try {
      this.publishFor(operation, {
        status: "saving",
        generation: operation.generation,
        attempt: operation.attempt,
        navigation: "blocked"
      });
    } catch (error: unknown) {
      operation.inFlight = null;
      deferred.reject(error);
      return tracked;
    }

    const work = this.persistProgress(operation).then(
      (progress): AttemptSaveCommandResult<TPayload> => {
        const settled: AttemptSaveSettledState<TPayload> = this.isPersistent
          ? {
              status: "saved",
              generation: operation.generation,
              progress,
              navigation: "durable"
            }
          : {
              status: "temporary",
              generation: operation.generation,
              progress,
              navigation: "temporary"
            };

        operation.settled = settled;
        if (
          this.pendingByProfile.get(operation.context.profileId) === operation
        ) {
          this.pendingByProfile.delete(operation.context.profileId);
        }
        this.publishFor(operation, settled);
        return this.resultForCurrentGeneration(operation, settled);
      },
      (error: unknown): AttemptSaveCommandResult<TPayload> => {
        const settled: AttemptSaveErrorState = {
          status: "error",
          generation: operation.generation,
          attempt: operation.attempt,
          attemptSaveConfirmed: operation.attemptSaveConfirmed,
          error: toError(error),
          failedStage: operation.stage,
          navigation: "blocked",
          recoverable: true
        };

        operation.settled = settled;
        this.publishFor(operation, settled);
        return this.resultForCurrentGeneration(operation, settled);
      }
    );

    void work.then(
      (result) => {
        if (operation.inFlight === tracked) {
          operation.inFlight = null;
        }
        deferred.resolve(result);
      },
      (error: unknown) => {
        if (operation.inFlight === tracked) {
          operation.inFlight = null;
        }
        deferred.reject(error);
      }
    );

    return tracked;
  }

  private async persistProgress(
    operation: SaveOperation<TPayload>
  ): Promise<AttemptProgressUpdate<TPayload>> {
    if (!operation.attemptSaveConfirmed) {
      operation.stage = "save-attempt";
      await this.repository.saveAttempt(operation.attempt);
      operation.attemptSaveConfirmed = true;
    }

    if (operation.attempts === null) {
      operation.stage = "load-attempts";
      const attempts = await this.repository.listAttempts(
        operation.context.profileId
      );

      if (!attempts.some(({ id }) => id === operation.attempt.id)) {
        throw new Error("The saved attempt is not visible in the repository.");
      }

      operation.attempts = attempts;
    }

    if (operation.mastery === null) {
      operation.stage = "save-mastery";
      operation.mastery = calculateMasteryForProfile(
        operation.context.profileId,
        operation.attempts
      );
    }

    if (!operation.masterySaveConfirmed) {
      operation.stage = "save-mastery";
      await this.repository.saveMastery(
        operation.context.profileId,
        operation.mastery
      );
      operation.masterySaveConfirmed = true;
    }

    if (operation.rewardUpdate === null) {
      operation.stage = "load-rewards";
      const currentRewards = await this.repository.getRewards(
        operation.context.profileId
      );
      operation.rewardUpdate = awardCompletedTaskRewards(currentRewards);
    }

    if (!operation.rewardsSaveConfirmed) {
      operation.stage = "save-rewards";
      await this.repository.saveRewards(
        operation.context.profileId,
        operation.rewardUpdate.rewards
      );
      operation.rewardsSaveConfirmed = true;
    }

    operation.stage = "derive-progress";
    const dailySession = operation.context.dailySession;
    const sessionProgress =
      dailySession === undefined || dailySession === null
        ? null
        : completeSessionTask(dailySession, operation.attempt.levelId);
    const nextRecommendation =
      rankLevelsForExploration({
        profileId: operation.context.profileId,
        levels: operation.context.recommendationLevels,
        attempts: operation.attempts,
        mastery: operation.mastery
      })[0] ?? null;

    return {
      attempt: operation.attempt,
      mastery: operation.mastery,
      rewardUpdate: operation.rewardUpdate,
      sessionProgress,
      nextRecommendation
    };
  }

  private isCurrent(generation: AttemptGeneration): boolean {
    return this.current?.generation === generation;
  }

  private staleResult(generation: AttemptGeneration): StaleAttemptCompletion {
    return { status: "stale", generation };
  }

  private resultForCurrentGeneration(
    operation: SaveOperation<TPayload>,
    settled: AttemptSaveSettledState<TPayload>
  ): AttemptSaveCommandResult<TPayload> {
    return this.isCurrent(operation.generation)
      ? settled
      : this.staleResult(operation.generation);
  }

  private publishFor(
    operation: SaveOperation<TPayload>,
    snapshot: AttemptSaveSnapshot<TPayload>
  ): void {
    if (this.isCurrent(operation.generation)) {
      this.publish(snapshot);
    }
  }

  private publish(snapshot: AttemptSaveSnapshot<TPayload>): void {
    this.snapshot = snapshot;

    for (const listener of this.listeners) {
      listener();
    }
  }
}
